import dayjs from "dayjs";
import { useEffect, useState } from "react";
import styled from "styled-components";
import LineBtn from "../../components/Button/LineBtn";
import { OptionType } from "../../components/Filters/DropDown";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import Pagination from "../../components/Table/Pagination";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import { Outlet, useNavigate } from "react-router-dom";
import LineBox from "../../components/DefaultStyles/LineBox";
import { useApi } from "../../hooks/useApi";
import ExcelDownload from "../../components/DefaultStyles/ExcelDownload";
import Loader from "../../components/DefaultStyles/Loader";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { authType } from "../Login";
import UserInfoFilter from "../../components/pages/Users/UserInfoFilter";
import SpaceBetween from "../../components/DefaultStyles/SpaceBetween";
import { getThousandComma } from "../../Api/ApiCommon";

interface IUserInfo {
  birthday: string;
  escaped: boolean;
  indate: string;
  name: string;
  nickname: string;
  phone: string;
  uid: string;
  isParty: number;
  isMain: boolean;
}

interface SFilters {
  marginBottom?: number;
}

export const userInfoFilterState = atom({
  key: "userInfoFilterState",
  default: {
    filterType: {
      value: "",
      label: "",
    },
    searchValue: "",
    startDate: "",
    endDate: "",
    page: 0,
  },
});

export default function UserInfo() {
  const navigate = useNavigate();
  const auth = useRecoilValue(authType);
  const [userInfoFilter, setUserInfoFilter] =
    useRecoilState(userInfoFilterState);

  const [dropDownValue, setDropDownValue] = useState<OptionType>({
    value:
      userInfoFilter?.filterType?.value !== ""
        ? userInfoFilter?.filterType?.value
        : "",
    label:
      userInfoFilter?.filterType?.label !== ""
        ? userInfoFilter?.filterType?.label
        : "선택",
  });
  const [searchValue, setSearchValue] = useState<string | undefined>(
    userInfoFilter?.searchValue !== "" ? userInfoFilter?.searchValue : "",
  );
  const [filter, setFilter] = useState({
    startDate:
      userInfoFilter?.startDate !== "" ? userInfoFilter?.startDate : "",
    endDate: userInfoFilter?.endDate !== "" ? userInfoFilter?.endDate : "",
  });
  const [page, setPage] = useState(userInfoFilter?.page ?? 0);

  useEffect(() => {
    setUserInfoFilter((prev) => ({ ...prev, page: 0 }));
  }, []);

  const { response, loading } = useApi({
    method: "get",
    url:
      `user?page=${page}&size=10` +
      (searchValue !== "" ? `&${dropDownValue?.value}=${searchValue}` : ``) +
      (filter?.endDate !== ""
        ? `&startdate=${filter?.startDate}&enddate=${dayjs(filter?.endDate)
            ?.add(1, "day")
            ?.format("YYYY-MM-DD")}`
        : ``),
  });

  const tableHead = [
    "UID",
    "이름",
    "닉네임",
    "전화번호",
    "생년월일",
    "가입 일시",
    "비고",
    "상세",
  ];

  useEffect(() => {
    if (searchValue !== "") {
      setPage(0);
    }
  }, [searchValue]);

  return (
    <>
      <PageTitle title="회원 정보" />
      <UserInfoFilter
        setFilter={setFilter}
        setDropDownValue={setDropDownValue}
        dropDownValue={dropDownValue}
        setSearchValue={setSearchValue}
        userInfoFilter={userInfoFilter}
        setPage={setPage}
      />
      <LineBox>
        <Table
          tableHeadData={tableHead}
          id="user-info-table"
          tableMinWidth={750}
        >
          <>
            {response?.data?.items?.map((d: IUserInfo) => {
              return (
                <TableBody key={d?.uid}>
                  
                  <TableBodyItem style={{borderWidth:1}}>
                    {d?.uid}
                    {d?.isMain ? "" : <div className="material-icons-outlined" style={{verticalAlign: "sub", fontSize:18}}>child_care</div>}
                  </TableBodyItem>
                  <TableBodyItem>{d?.name}</TableBodyItem>
                  <TableBodyItem isKeepAll={true}>
                    {d?.nickname}
                    {d?.isParty > 0 && (
                      <ActLogo
                        src={
                          d?.isParty === 1
                            ? require("../../assets/ic_activation.png")
                            : require("../../assets/ic_activation2.png")
                        }
                      />
                    )}
                  </TableBodyItem>
                  <TableBodyItem>
                    {d?.phone !== "" ? d?.phone : "-"}
                  </TableBodyItem>
                  <TableBodyItem>
                    {d?.escaped
                      ? d?.birthday
                      : dayjs(d?.birthday).format("YYYY-MM-DD")}
                  </TableBodyItem>
                  <TableBodyItem>
                    {dayjs(d?.indate).format("YYYY-MM-DD HH:mm")}
                  </TableBodyItem>
                  <TableBodyItem>{d?.escaped ? "탈퇴" : "-"}</TableBodyItem>
                  <TableBodyItem>
                    <LineBtn
                      btnText="상세"
                      btnClickEvent={() => {
                        setUserInfoFilter({
                          filterType: {
                            label: dropDownValue?.label ?? "",
                            value: dropDownValue?.value ?? "",
                          },
                          searchValue: searchValue ?? "",
                          startDate: filter?.startDate ?? "",
                          endDate: filter?.endDate ?? "",
                          page,
                        });

                        navigate(
                          `/users/info/${d?.uid}`,
                          searchValue !== ""
                            ? {
                                state: {
                                  query: `${dropDownValue?.value}=${searchValue}`,
                                },
                              }
                            : undefined,
                        );
                      }}
                      disabled={!auth?.userInfo}
                    />
                  </TableBodyItem>
                </TableBody>
              );
            })}
          </>
        </Table>

        <SpaceBetween>
          <Pagination
            totalPage={response?.status === 200 ? response.data.totalPage : 1}
            currentPage={page}
            pageClickEvent={(index) => setPage(index)}
          />

          <TotalCount>
            총{" "}
            {getThousandComma(
              response?.status === 200 ? +response?.data?.totalCount : 0,
            )}
            개
          </TotalCount>
        </SpaceBetween>
      </LineBox>
      <ExcelDownload
        tableHead={tableHead}
        pushedData={(e: any) => {
          return [
            e.uid,
            e.name,
            e.nickname,
            e.phone,
            dayjs(e?.birthday).format("YYYY-MM-DD"),
            dayjs(e.indate)?.format("YYYY-MM-DD HH:mm"),
            e?.escaped ? "탈퇴" : "-",
          ];
        }}
        dataUrl={
          `user?page=0&size=1000` +
          (searchValue !== ""
            ? `&${dropDownValue?.value}=${searchValue}`
            : ``) +
          (filter?.endDate !== ""
            ? `&startdate=${filter?.startDate}&enddate=${dayjs(filter?.endDate)
                ?.add(1, "day")
                ?.format("YYYY-MM-DD")}`
            : ``)
        }
        disabled={response?.data?.items?.length < 1 || !response?.data?.items}
        title="회원 정보"
      />
      {loading && <Loader />}
      <Outlet />
    </>
  );
}

export const Filters = styled.div<SFilters>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ marginBottom }) => marginBottom ?? 12}px;
  gap: 10px;
`;

export const TotalCount = styled.span`
  margin-top: 24px;
  font-size: 14px;
  color: #858c97;
`;

const ActLogo = styled.img`
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;
  margin-bottom: 2px;
  margin-left: 2px;
`;
